import client, { builder } from "utils/graphql";
import { IDefinition, IGraphQLOptions, ICustomQueryOptions } from "types/utils/graphql";
import { isNull, get, set, omit } from "utils/lodash";
import helper from "utils/helper";
import { match } from "utils/renderPropsComposer";
import type { FetchPolicy, MutationFetchPolicy } from "@apollo/client";
export default class BaseService {
  name: any;
  collection: any;
  static DEFINITION: IDefinition;
  getBaseQuery(selections: string) {
    const defs = (this.constructor as typeof BaseService).DEFINITION;

    return builder.getBaseQuery(selections, defs);
  }
  getFindQuery(selections: string) {
    const defs = (this.constructor as typeof BaseService).DEFINITION;
    return builder.getFindQuery(selections, defs);
  }
  getUpdateQuery(selections: string) {
    const defs = (this.constructor as typeof BaseService).DEFINITION;
    return builder.getUpdateQuery(selections, defs);
  }
  getCreateQuery(selections: string) {
    const defs = (this.constructor as typeof BaseService).DEFINITION;
    return builder.getCreateQuery(selections, defs);
  }
  getDeleteQuery(selections: string) {
    const defs = (this.constructor as typeof BaseService).DEFINITION;
    return builder.getDeleteQuery(selections, defs);
  }
  getCustomGQL(query: string) {
    return builder.getCustomGQL(query);
  }

  buildOptions(options: IGraphQLOptions = {}, isMutation: boolean = false): any {
    const normalizedOptions = helper.normalizeParams(options);

    let fetchPolicy = get(options, "fetchPolicy");

    // Mutations don't support 'cache-first', 'cache-only', or 'standby'
    if (isMutation && fetchPolicy && ['cache-first', 'cache-only', 'standby'].includes(fetchPolicy as string)) {
      fetchPolicy = 'network-only' as MutationFetchPolicy;
    }

    let result: any = {
      ...(fetchPolicy && {
        fetchPolicy,
      }),
      ...(get(options, "errorPolicy") && {
        errorPolicy: get(options, "errorPolicy"),
      }),
    };
    if (!!normalizedOptions.token) {
      set(
        result,
        "context.headers.Authorization",
        `Bearer ${normalizedOptions.token}`
      );
      result = omit(result, "token");
    }
    // Handle variables explicitly if provided
    if (options.variables !== undefined) {
      set(result, "variables", options.variables);
    } else if (
      !normalizedOptions.variables &&
      !!omit(options, ["token", "fetchPolicy", "errorPolicy", "log"])
    ) {
      const autoVariables = {
        ...omit(options, ["token", "fetchPolicy", "errorPolicy", "log"]),
      };
      set(result, "variables", autoVariables);
    }
    return result;
  }
  pickParams(params: any) {
    const result = {
      total: get(params, "totalDocs"),
      totalPage: get(params, "totalPages"),
      limit: get(params, "limit"),
      page: get(params, "page"),
      offset: get(params, "offset"),
    };

    return result;
  }
  handleCustomResponse(res: any, paths: object) {
    let result = res;
    const path = get(paths, `DATA`);
    const pathParams = get(paths, `PARAMS`);
    const pathError = get(paths, `ERRORS`);
    
    if (!!pathParams) {
      const params = this.pickParams(get(res, pathParams));
      result = { ...result, params };
    }
    
    if (!!path && !!get(result, path)) {
      const data = get(res, path);
      result = { ...result, data };
    }
    
    if (!!pathError && !!get(result, pathError)) {
      const errors = get(res, pathError);
      result = { ...result, errors };
    }

    return result;
  }
  handleResponse(res: any, gqlAction: string) {
    let result = res;
    const defs = (this.constructor as typeof BaseService).DEFINITION;
    const path = get(defs, `GQL_RESPONSE_DATA_PATH.${gqlAction}`);
    const pathParams = get(defs, `GQL_RESPONSE_DATA_PATH.PARAMS`);
    if (!!pathParams)
      result = { ...result, params: this.pickParams(get(res, pathParams)) };
    if (!!path && !!get(result, path))
      result = { ...result, data: get(res, path) };
    return result;
  }
  async id(id: string, selections: string = "", options: IGraphQLOptions = {}) {
    const query = this.getBaseQuery(selections);
    if (isNull(query)) return false;
    const opts = this.buildOptions({ ...options, id });
    return this.handleResponse(
      await client.query({ query, ...opts }),
      "BASE"
    );
  }
  async find(options: IGraphQLOptions = {}, selections: string = "") {
    const defs = (this.constructor as typeof BaseService).DEFINITION;
    const query = this.getFindQuery(selections);
    if (isNull(query)) return false;
    const opts = this.buildOptions(options);

    try {
      const result = await client.query({ query, ...opts });
      return this.handleResponse(result, "FIND");
    } catch (error: any) {
      // If we have partial data with errors and errorPolicy is 'all', return the partial data
      if (opts.errorPolicy === 'all' && error?.networkError?.result) {
        console.warn("GraphQL returned partial data with errors:", error.graphQLErrors);
        return this.handleResponse(error.networkError.result, "FIND");
      }
      throw error;
    }
  }
  async create(options: IGraphQLOptions = {}, selections: string = "") {
    const defs = (this.constructor as typeof BaseService).DEFINITION;
    const query = this.getCreateQuery(selections);
    if (isNull(query)) return false;
    const opts = this.buildOptions(options, true);
    return this.handleResponse(
      await client.mutate({ mutation: query, ...opts }),
      "CREATE"
    );
  }
  async update(options: IGraphQLOptions = {}, selections: string = "") {
    const query = this.getUpdateQuery(selections);
    if (isNull(query)) return false;
    const opts = this.buildOptions(options, true);
    return this.handleResponse(
      await client.mutate({ mutation: query, ...opts }),
      "UPDATE"
    );
  }
  async delete(options: IGraphQLOptions = {}, selections: string = "") {
    const query = this.getDeleteQuery(selections);
    if (isNull(query)) return false;
    const opts = this.buildOptions(options, true);
    return this.handleResponse(
      await client.mutate({ mutation: query, ...opts }),
      "DELETE"
    );
  }
  async custom(options: ICustomQueryOptions = {}, customQuery: string) {
    const log = get(options, "log", false);

    const query = this.getCustomGQL(customQuery);

    if (isNull(query)) return false;
    const { typeQL = "query", PATH_RESPONSE = {}, ...customOptions } = options;
    const opts = this.buildOptions(customOptions, typeQL === "mutation");
    if (log) {

    }
    const resultCustomResponse = // console.log("customQuery", customQuery, { opts });
      this.handleCustomResponse(
        await match(
          {
            isTrue: typeQL === "mutation",
            value: async () => {
              try {
                const response = await client.mutate({ mutation: query, ...opts });
                return response;
              } catch (errors) {
                return { errors };
              }
            },
          },
          {
            isTrue: typeQL === "query",
            value: async () => {
              try {
                return await client.query({ query, ...opts });
              } catch (errors) {
                return { errors };
              }
            },
          }
        )(),
        PATH_RESPONSE
      );
    return resultCustomResponse;
  }
}
